// Pure spec → backend QuerySpec builders. Extracted from DashboardContext to slim it.
// Each widget type maps its spec (date range, subjects, metrics) to the query array the
// backend expects; buildQueriesForWidget dispatches by widget type. No React/state deps.

import type {
  ControllerBindingFields,
  ControllerKind,
  DateSpecJSON,
  ComponentSpec,
  ChartComponentSpec,
  TableComponentSpec,
  BodyMapComponentSpec,
  Table2DComponentSpec,
  GaugeComponentSpec,
  RadarComponentSpec,
  PieChartComponentSpec,
  PlayerNotesComponentSpec,
  StatComponentSpec,
  ScatterplotComponentSpec,
} from "./types";
import { bindingField } from "./controllerBindings";
import { resolveSeriesMisc } from "./seriesMisc";
import { DEFAULT_LATEST_N, trainingCountForPreset } from "../AddWidgetModal/constants";
import type { CountSpecMisc } from "./types/builderMisc";
// The pure count rules live in a .mjs module so they can be unit-tested without React
// or a store, the same arrangement Utils/crossOriginCacheBust.mjs uses.
import {
  countFields as countFieldsRaw,
  countSourceFromMetricId,
  defaultCountSpec,
  identifierType,
  metricKeyFor,
} from "./countSpec.mjs";
// The one preset table, shared with the slicer titles and the PDF snapshot header.
import { PRESET_OFFSETS } from "./datePresets.mjs";

/** `countFields` shaped for a spread into a query literal: `{}` when there is none. */
function countFields(
  spec: CountSpecMisc | undefined,
  metricId?: string | null,
): Record<string, unknown> {
  // A count identifier with no spec is rejected outright ("a count metric identifier
  // needs a 'count' spec"). Five call sites set the id and the spec together, but that
  // invariant is held by convention; a saved spec that lost its countSpec would 400 the
  // whole dashboard. Fall back to counting everything, which is what an empty
  // include-list means anyway.
  const source = countSourceFromMetricId(metricId);
  const effective = spec ?? (source ? defaultCountSpec(source) : undefined);
  const fields = countFieldsRaw(effective);
  return fields ? { count: fields } : {};
}

/**
 * The `count` spec a count query carries, or nothing at all for a measured metric.
 *
 * The backend rejects a count spec on a measured identifier and a count identifier
 * with no spec, so the two always travel together. `reading` is injuries-only — the
 * backend refuses it on any other source, so it is omitted rather than defaulted.
 *
 * Radar never calls this: its axes normalise against a range a count has none of, and
 * the backend rejects a count there.
 */

/**
 * The controllers each filter section may legally follow, by kind. `serializeDashboardSpec`
 * derives it from the dashboard's controller widgets and passes it down, so a stale binding
 * is never stamped onto a saved query — the backend rejects dangling references.
 */
export type EligibleControllers = Record<ControllerKind, Set<string>>;

const NO_CONTROLLERS: EligibleControllers = {
  date: new Set(), subject: new Set(), single_date: new Set(), single_player: new Set(),
};

/**
 * The reference fields for one query. The query keeps its own dormant date/subject values
 * alongside: they are the restore state in the UI and the engine's fallback.
 */
function controllerRefs(
  dateMisc: ControllerBindingFields | undefined,
  subjectMisc: ControllerBindingFields | undefined,
  eligible: EligibleControllers,
): Record<string, string> {
  const refs: Record<string, string> = {};
  const stamp = (
    kind: ControllerKind,
    misc: ControllerBindingFields | undefined,
    field: string,
  ) => {
    const uuid = misc?.[bindingField(kind)];
    if (!uuid || !eligible[kind].has(uuid)) return;
    refs[field] = uuid;
    // The mode rides with the subject reference: the engine needs it to know whether to
    // substitute the controller's athletes or the positions they play.
    if (kind === "subject" && misc?.subjectControllerMode === "positions") {
      refs.subject_controller_mode = "positions";
    }
  };
  stamp("date", dateMisc, "date_controller_id");
  stamp("subject", subjectMisc, "subject_controller_id");
  return refs;
}

function mapGroupBy(groupBy: string): Record<string, unknown>[] {
  switch (groupBy) {
    case "day":
      return [{ field: "time", grain: "day" }];
    case "week":
      return [{ field: "time", grain: "week" }];
    case "month":
      return [{ field: "time", grain: "month" }];
    case "year":
      return [{ field: "time", grain: "year" }];
    case "player":
      return [{ field: "player" }];
    case "position":
      return [{ field: "pos" }];
    case "drill":
      return [{ field: "drill" }];
    default:
      return [];
  }
}

// ── Build a DateSpec from form fields ────────────────────────

interface DateFilterFields {
  showAdvancedDate?: boolean;
  startDateType?: "relative" | "absolute" | "latest_n";
  startDateAbsolute?: string;
  startDateOffset?: number;
  startDateLatestN?: number;
  endDateType?: "relative" | "absolute";
  endDateAbsolute?: string;
  endDateOffset?: number;
  dayFilter?: string[];
  dateRangePreset?: string;
  dateRange?: string;
}

export function buildDateFilter(fields: DateFilterFields): DateSpecJSON {
  const preset = fields.dateRangePreset ?? fields.dateRange;
  const presetOffset = preset ? (PRESET_OFFSETS[preset] ?? -28) : -28;

  // When not using advanced/custom date mode, derive offsets purely from the
  // preset. The stored startDateOffset/endDateOffset carry form defaults that
  // must not override the preset selection.
  if (!fields.showAdvancedDate) {
    // "Last 5 Trainings" counts dates that HAVE data, so it is the same window the
    // Advanced mode builds — the preset just picks the count for you.
    const trainingCount = trainingCountForPreset(preset);
    return {
      start_date: trainingCount != null
        ? { type: "latest_n", n: trainingCount }
        : { type: "relative", anchor: "today", offset_days: presetOffset },
      end_date: { type: "relative", anchor: "today", offset_days: 0 },
      day_filter: fields.dayFilter ?? [],
    };
  }

  const startType = fields.startDateType ?? "relative";
  const endType = fields.endDateType ?? "relative";

  // latest_n is a window the DATA defines — "the last 5 sessions". The backend resolves
  // it per render, so the saved spec carries the count and never a date: resolving it here
  // would freeze the widget to whichever dates existed on the day it was saved.
  const start_date: DateSpecJSON["start_date"] =
    startType === "latest_n"
      ? { type: "latest_n", n: fields.startDateLatestN ?? DEFAULT_LATEST_N }
      : startType === "absolute"
        ? { type: "absolute", value: fields.startDateAbsolute ?? "" }
        : { type: "relative", anchor: "today", offset_days: fields.startDateOffset ?? presetOffset };

  // Advanced date filters offers no To row — the window ends today — so the stored end
  // is ignored there. Honouring it would let an absolute date left behind by the other
  // mode anchor the window to a day the coach can no longer see.
  const end_date: DateSpecJSON["end_date"] =
    startType === "latest_n"
      ? { type: "relative", anchor: "today", offset_days: 0 }
      : endType === "absolute"
        ? { type: "absolute", value: fields.endDateAbsolute ?? "" }
        : { type: "relative", anchor: "today", offset_days: fields.endDateOffset ?? 0 };

  return {
    start_date,
    end_date,
    day_filter: fields.dayFilter ?? [],
  };
}

// ── Build QuerySpec array for a widget ───────────────────────

function buildQueriesForChart(
  spec: ChartComponentSpec,
  eligible: EligibleControllers,
): Record<string, unknown>[] {
  const groupBy = spec.chart_builder_misc?.groupBy ?? "day";

  return spec.series.map((s): Record<string, unknown> | null => {
    const misc = s.series_builder_misc;
    const metricId = misc.selectedMetricId;
    if (!metricId) return null;

    const metricKey = metricKeyFor(metricId, s.metricAgg);
    const { date: dateFields, subject: subjectFields } = resolveSeriesMisc(s, spec.series);

    const selectedSubjects = subjectFields?.selectedSubjects ?? [];
    const selectedDrills = subjectFields?.selectedDrills ?? [];
    const subjects = selectedSubjects.length > 0 || selectedDrills.length > 0
      ? [...selectedSubjects, ...selectedDrills]
      : ["full-team"];

    return {
      id: s.queryId,
      metric_identifier: {
        key: metricKey,
        type: identifierType(misc?.selectedMetricId, s.metricIdentifierType),
      },
      ...countFields(misc?.countSpec, metricId),
      filter: {
        date_filter: buildDateFilter(dateFields ?? {}),
        subject_filter: subjects,
        extra_filters: { exclude_injured_players: subjectFields?.excludeInjuredPlayers ?? true },
        ...controllerRefs(dateFields, subjectFields, eligible),
      },
      groupby: mapGroupBy(groupBy),
    };
  }).filter((q): q is Record<string, unknown> => q !== null);
}

function buildQueriesForTable(
  spec: TableComponentSpec,
  eligible: EligibleControllers,
): Record<string, unknown>[] {
  const misc = spec.table_builder_misc;
  const groupBy = misc?.groupBy ?? "day";
  const columnMetrics = misc?.columnMetrics ?? {};
  const selectedSubjects = misc?.selectedSubjects ?? [];
  const selectedDrills = misc?.selectedDrills ?? [];
  const subjects = selectedSubjects.length > 0 || selectedDrills.length > 0
    ? [...selectedSubjects, ...selectedDrills]
    : ["full-team"];

  return spec.series.map((s): Record<string, unknown> | null => {
    const metricId = columnMetrics[s.queryId];
    if (!metricId) return null;

    const metricKey = metricKeyFor(metricId, s.metricAgg);

    return {
      id: s.queryId,
      metric_identifier: {
        key: metricKey,
        type: identifierType(metricId, s.metricIdentifierType),
      },
      ...countFields(misc?.countSpecs?.[s.queryId], metricId),
      filter: {
        date_filter: buildDateFilter(misc ?? {}),
        subject_filter: subjects,
        extra_filters: { exclude_injured_players: misc?.excludeInjuredPlayers ?? true },
        ...controllerRefs(misc, misc, eligible),
      },
      groupby: mapGroupBy(groupBy),
    };
  }).filter((q): q is Record<string, unknown> => q !== null);
}

function buildQueriesForGauge(
  spec: GaugeComponentSpec,
  eligible: EligibleControllers,
): Record<string, unknown>[] {
  const misc = spec.gauge_builder_misc;
  if (!spec.metric) return [];

  const needleSubjects =
    (misc.selectedSubjects?.length ?? 0) > 0 || (misc.selectedDrills?.length ?? 0) > 0
      ? [...(misc.selectedSubjects ?? []), ...(misc.selectedDrills ?? [])]
      : ["full-team"];

  const metricKey = metricKeyFor(misc.selectedMetricId, misc.metricAgg ?? "avg");

  const queries: Record<string, unknown>[] = [
    {
      id: spec.queryId,
      metric_identifier: { key: metricKey, type: identifierType((misc as any)?.selectedMetricId, spec.metricIdentifierType) },
      ...countFields((misc as any)?.countSpec, (misc as any)?.selectedMetricId),
      filter: {
        date_filter: buildDateFilter(misc as any),
        subject_filter: needleSubjects,
        extra_filters: { exclude_injured_players: misc.excludeInjuredPlayers ?? true },
        ...controllerRefs(misc, misc, eligible),
      },
      groupby: [],
    },
  ];

  if (spec.scaleQueryId) {
    const scaleSubjects = misc.scaleSubjectFilter?.length
      ? misc.scaleSubjectFilter
      : ["full-team"];

    // The scale's aggregation comes from the scale TYPE (e.g. "team_best" → best),
    // not the needle's agg — otherwise "team_best" and "team_avg" query identically.
    const scaleAgg = spec.scale.type.endsWith("_best")
      ? "best"
      : spec.scale.type.endsWith("_sum")
        ? "sum"
        : "avg";
    // A count only ever totals: the backend rejects `count-flags-avg`. The scale's
    // team_best / team_avg variants are meaningless for one anyway — the scale of a
    // count is its total over the window.
    const isCountMetric = identifierType((misc as any)?.selectedMetricId) === "count";
    const scaleMetricKey = `${misc.selectedMetricId}-${isCountMetric ? "sum" : scaleAgg}`;

    queries.push({
      id: spec.scaleQueryId,
      metric_identifier: {
        key: scaleMetricKey,
        type: identifierType((misc as any)?.selectedMetricId, spec.metricIdentifierType),
      },
      ...countFields((misc as any)?.countSpec, (misc as any)?.selectedMetricId),
      filter: {
        date_filter: buildDateFilter(misc as any),
        subject_filter: scaleSubjects,
        extra_filters: { exclude_injured_players: misc.excludeInjuredPlayers ?? true },
        ...controllerRefs(misc, undefined, eligible),
      },
      groupby: [],
      metadata: { scale_type: spec.scale.type },
    });
  }

  return queries;
}

function buildQueriesForTable2D(
  spec: Table2DComponentSpec,
  eligible: EligibleControllers,
): Record<string, unknown>[] {
  const misc = (spec.table_2d_builder_misc ?? {}) as any;
  const miscMetrics = (misc.metrics ?? []) as Array<{
    queryId: string;
    metricId: string | null;
    metricAgg: string;
  }>;

  const selectedSubjects = (misc.selectedSubjects ?? []) as string[];
  const selectedDrills = (misc.selectedDrills ?? []) as string[];
  const subjects =
    selectedSubjects.length > 0 || selectedDrills.length > 0
      ? [...selectedSubjects, ...selectedDrills]
      : ["full-team"];
  const filter = {
    date_filter: buildDateFilter(misc),
    subject_filter: subjects,
    extra_filters: { exclude_injured_players: misc.excludeInjuredPlayers ?? true },
    ...controllerRefs(misc, misc, eligible),
  };

  const rowDim = spec.rowDimension;
  const colDim = spec.colDimension;

  if (rowDim !== "metric" && colDim !== "metric") {
    // Non-metric pair: one QuerySpec with both dimensions as groupby fields.
    // week × weekday is the exception — a single day-grain time groupby; the
    // backend derives the week and the weekday from each date.
    const m = miscMetrics[0];
    if (!m?.metricId) return [];
    const isWeekWeekday = rowDim === "weekday" || colDim === "weekday";
    return [{
      id: spec.queryIds[0],
      metric_identifier: { key: metricKeyFor(m.metricId, m.metricAgg), type: identifierType(m.metricId) },
      ...countFields(misc?.countSpecs?.[spec.queryIds[0]], m.metricId),
      filter,
      groupby: isWeekWeekday
        ? [{ field: "time", grain: "day" }]
        : [...mapGroupBy(rowDim), ...mapGroupBy(colDim)],
    }];
  }

  // entity/time × metric: one QuerySpec per metric
  const nonMetricDim = rowDim === "metric" ? colDim : rowDim;
  return spec.queryIds.flatMap((qid, i) => {
    const m = miscMetrics[i];
    if (!m?.metricId) return [];
    return [{
      id: qid,
      metric_identifier: { key: metricKeyFor(m.metricId, m.metricAgg), type: identifierType(m.metricId) },
      ...countFields(misc?.countSpecs?.[qid], m.metricId),
      filter,
      groupby: mapGroupBy(nonMetricDim),
    }];
  });
}

function buildQueriesForRadar(
  spec: RadarComponentSpec,
  eligible: EligibleControllers,
): Record<string, unknown>[] {
  const misc = spec.radar_builder_misc ?? {};
  const radarMetrics = misc.metrics ?? [];
  if (radarMetrics.length === 0) return [];

  const queries: Record<string, unknown>[] = [];
  const dateFilter = buildDateFilter(misc as any);
  const isMetricVertex = (spec.vertexDimension ?? "metric") === "metric";

  for (const seriesDef of spec.series) {
    for (let metricIdx = 0; metricIdx < radarMetrics.length; metricIdx++) {
      const metric = radarMetrics[metricIdx];
      const qid = seriesDef.queryIds[metricIdx];
      if (!qid || !metric.metricId) continue;
      // Custom range is per-metric when metrics are the axes (heterogeneous units),
      // and a single global range when entities are the axes (all axes share one metric).
      // Unset per-metric bounds are sent as-is; the backend treats a non-positive
      // range as "unconfigured" and scores that axis at the neutral midpoint.
      const customRange =
        spec.normalization === "custom"
          ? isMetricVertex
            ? { custom_min: metric.customMin, custom_max: metric.customMax }
            : { custom_min: spec.customMin, custom_max: spec.customMax }
          : undefined;
      queries.push({
        id: qid,
        metric_identifier: {
          key: `${metric.metricId}-${metric.metricAgg}`,
          type: metric.metricIdentifierType ?? "metric",
        },
        filter: {
          date_filter: dateFilter,
          subject_filter: [seriesDef.subjectFilter],
          extra_filters: { exclude_injured_players: false },
          // Radar subjects are per-series vertices — only its date section can be governed.
          ...controllerRefs(misc, undefined, eligible),
        },
        groupby: [{ field: "player" }],
        metadata: {
          radar_normalization: spec.normalization,
          ...customRange,
        },
      });
    }
  }
  return queries;
}

function buildQueriesForPie(
  spec: PieChartComponentSpec,
  eligible: EligibleControllers,
): Record<string, unknown>[] {
  const firstMisc = spec.series[0]?.series_builder_misc;
  return spec.series.flatMap((s) => {
    const misc = s.series_builder_misc;
    if (!misc.selectedMetricId) return [];
    // A pie series that inherits the date range follows series[0]'s binding too.
    const dateMisc = misc.inheritDateRange === false ? misc : (firstMisc ?? misc);
    const metricKey = metricKeyFor(misc.selectedMetricId, s.metricAgg);
    const selectedSubjects = misc.selectedSubjects ?? [];
    const selectedDrills = misc.selectedDrills ?? [];
    const subjects =
      selectedSubjects.length > 0 || selectedDrills.length > 0
        ? [...selectedSubjects, ...selectedDrills]
        : ["full-team"];
    return [{
      id: s.queryId,
      metric_identifier: { key: metricKey, type: identifierType(misc.selectedMetricId, s.metricIdentifierType) },
      ...countFields(misc?.countSpec, misc.selectedMetricId),
      filter: {
        date_filter: buildDateFilter(misc),
        subject_filter: subjects,
        extra_filters: { exclude_injured_players: misc.excludeInjuredPlayers ?? true },
        ...controllerRefs(dateMisc, misc, eligible),
      },
      groupby: [],
    }];
  });
}

function buildQueriesForStat(
  spec: StatComponentSpec,
  eligible: EligibleControllers,
): Record<string, unknown>[] {
  const misc = spec.stat_tile_builder_misc;
  if (!misc?.selectedMetricId) return [];

  const metricKey = metricKeyFor(misc.selectedMetricId, misc.metricAgg);
  const selectedSubjects = misc.selectedSubjects ?? [];
  const selectedDrills = misc.selectedDrills ?? [];
  const subjects =
    selectedSubjects.length > 0 || selectedDrills.length > 0
      ? [...selectedSubjects, ...selectedDrills]
      : ["full-team"];

  return [
    {
      id: spec.queryId,
      metric_identifier: { key: metricKey, type: identifierType(misc.selectedMetricId) },
      ...countFields(misc.countSpec, misc.selectedMetricId),
      filter: {
        date_filter: buildDateFilter(misc),
        subject_filter: subjects,
        session_filter: [],
        ...controllerRefs(misc, misc, eligible),
      },
      groupby: [],
      metadata: { comparison_key: misc.comparisonType, exclude_injured_players: misc.excludeInjuredPlayers ?? true, show_team_pos_data: misc.showTeamPosData !== false },
    },
  ];
}

function buildQueriesForPlayerNotes(
  spec: PlayerNotesComponentSpec,
  eligible: EligibleControllers,
): Record<string, unknown>[] {
  const misc = spec.player_notes_builder_misc;

  const extraFilters: Record<string, unknown> = {};
  if (misc?.noteIds?.length) extraFilters.note_ids = misc.noteIds;
  if (misc?.searchPattern) extraFilters.search_pattern = misc.searchPattern;
  extraFilters.exclude_injured_players = misc?.excludeInjuredPlayers ?? false;

  const selectedSubjects = misc?.selectedSubjects ?? [];
  const subjects = selectedSubjects.length > 0 ? selectedSubjects : ["full-team"];

  return [
    {
      id: spec.queryId,
      metric_identifier: null,
      filter: {
        date_filter: buildDateFilter(misc ?? {}),
        subject_filter: subjects,
        extra_filters: extraFilters,
        ...controllerRefs(misc, misc, eligible),
      },
      groupby: [],
    },
  ];
}

function buildQueriesForBodyMap(
  spec: BodyMapComponentSpec,
  eligible: EligibleControllers,
): Record<string, unknown>[] {
  const misc = spec.body_map_builder_misc ?? {};
  const playerSubject = misc.playerSubject ?? "";
  // Read as string to also handle legacy "latest"/"fixed" specs gracefully.
  const dateMode = (misc.dateMode ?? "relative") as string;

  let dateValue: Record<string, unknown>;
  let useLatest = false;
  if (dateMode === "absolute" || dateMode === "fixed") {
    dateValue = { type: "absolute", value: misc.fixedDate ?? "" };
  } else if (dateMode === "latest") {
    dateValue = { type: "relative", anchor: "today", offset_days: 0 };
    useLatest = true;
  } else {
    dateValue = { type: "relative", anchor: "today", offset_days: misc.dateOffset ?? 0 };
  }

  return [
    {
      id: spec.queryId,
      filter: {
        date_filter: { start_date: dateValue, end_date: dateValue, day_filter: [] },
        subject_filter: playerSubject ? [playerSubject] : [],
        extra_filters: { bodymap_use_latest: useLatest },
        ...(misc.singleDateControllerId && eligible.single_date.has(misc.singleDateControllerId)
          ? { single_date_controller_id: misc.singleDateControllerId }
          : {}),
        ...(misc.singlePlayerControllerId
          && eligible.single_player.has(misc.singlePlayerControllerId)
          ? { single_player_controller_id: misc.singlePlayerControllerId }
          : {}),
      },
      groupby: [{ field: "time", grain: "day" }],
    },
  ];
}

function buildQueriesForScatterplot(spec: ScatterplotComponentSpec): Record<string, unknown>[] {
  const misc = spec.scatterplot_builder_misc ?? {};
  const teamAverages = spec.widgetMutations.teamAverages === true;

  const selectedSubjects = misc.selectedSubjects ?? [];
  const selectedDrills = misc.selectedDrills ?? [];
  const subjects =
    selectedSubjects.length > 0 || selectedDrills.length > 0
      ? [...selectedSubjects, ...selectedDrills]
      : ["full-team"];

  // The backend pairs the X query and the Y query into dots, so it requires ONE
  // shared filter and ONE shared groupby: the entity (what one dot is) plus the
  // fixed day grain. teamAverages is the coach-facing face of a time-only groupby.
  const filter = {
    date_filter: buildDateFilter(misc),
    subject_filter: subjects,
    extra_filters: { exclude_injured_players: misc.excludeInjuredPlayers ?? true },
  };
  const groupby = teamAverages
    ? [{ field: "time", grain: "day" }]
    : [...mapGroupBy(misc.groupBy ?? "player"), { field: "time", grain: "day" }];

  const axes = spec.xAxisMode === "date" ? [spec.yAxis] : [spec.xAxis, spec.yAxis];
  return axes.flatMap((axis) => {
    const metricId = axis?.axis_builder_misc?.selectedMetricId;
    if (!axis || !metricId) return [];
    return [{
      id: axis.queryId,
      metric_identifier: { key: `${metricId}-${axis.metricAgg}`, type: "metric" },
      filter,
      groupby,
    }];
  });
}

export function buildQueriesForWidget(
  spec: ComponentSpec | undefined,
  eligible: EligibleControllers = NO_CONTROLLERS,
): Record<string, unknown>[] {
  if (!spec) return [];
  switch (spec.type) {
    case "chart":
      return buildQueriesForChart(spec, eligible);
    case "table":
      return buildQueriesForTable(spec, eligible);
    case "body_map":
      return buildQueriesForBodyMap(spec, eligible);
    case "gauge":
      return buildQueriesForGauge(spec, eligible);
    case "table_2d":
      return buildQueriesForTable2D(spec, eligible);
    case "radar":
      return buildQueriesForRadar(spec, eligible);
    case "pie_chart":
      return buildQueriesForPie(spec, eligible);
    case "stat_tile":
      return buildQueriesForStat(spec, eligible);
    case "player_notes":
      return buildQueriesForPlayerNotes(spec, eligible);
    case "scatterplot":
      return buildQueriesForScatterplot(spec);
    default:
      return [];
  }
}
